import type { SagaIterator } from 'redux-saga';
import {
  call,
  put,
  fork,
  take,
  select,
  SagaReturnType,
} from 'redux-saga/effects';
import Logger from '../../../services/Logger';
import { initCf } from '../../../services/centrifugo';
import {
  setCentrifugoConnected,
  setCentrifugoDisconnected,
  setCentrifugoError,
} from '../../actions/domain/centrifugo/state';
import {
  CentrifugoChannelValue,
  createCentrifugoChannel,
} from '../../channels/centrifugo';
import { publicationHandler, connectorsWatchers } from './steps';
import { centrifugoProjectsChecker } from './utils';
import { isLocalGameLaunchMode } from '../../reducers/app/initialization';
import { channelSelector, userTokenSelector } from '../../reducers/app/params';
import { centrifugoURLSelector } from '../../reducers/app/configuration';
import { CENTRIFUGO_EVENTS_MAP } from '../../constants/centrifugo';

export const logger = new Logger('Centrifugo', {
  prefixColor: '#7FFFD4',
});

/**
 * README
 * to add new project - you need to
 * 1. set up a feature and a project (search for FEATURES_CENTRIFUGO_PROJECTS_MAP)
 * 2. add handler for specific channel (search for PUBLICATION_HANDLERS_MAP)
 * 3. To add a specific handler for centrifugo 'connect' info - search for 'connectorsInitializer'
 */
export function* centrifugoInitializer(): SagaIterator<void> {
  // creating centrifugo instance
  const isReviewMode: ReturnType<typeof isLocalGameLaunchMode> = yield select(
    isLocalGameLaunchMode
  );
  const projects: SagaReturnType<typeof centrifugoProjectsChecker> = yield call(
    centrifugoProjectsChecker
  );
  // TODO: remove after transfer to new way of getting connection data
  const channel: ReturnType<typeof channelSelector> = yield select(
    channelSelector
  );
  const token: ReturnType<typeof userTokenSelector> = yield select(
    userTokenSelector
  );
  const centrifugoURL: ReturnType<typeof centrifugoURLSelector> = yield select(
    centrifugoURLSelector
  );

  if (!centrifugoURL || isReviewMode) return;

  const centrifugo: SagaReturnType<typeof initCf> = yield call(initCf, {
    centrifugoURL,
    channel,
    projects,
    token,
  });

  const centrifugoChannel: SagaReturnType<typeof createCentrifugoChannel> =
    yield call(createCentrifugoChannel, centrifugo);

  yield fork(connectorsWatchers, projects);

  try {
    while (true) {
      const action: CentrifugoChannelValue = yield take(centrifugoChannel);

      switch (action.type) {
        case CENTRIFUGO_EVENTS_MAP.CONNECT: {
          logger.log('Centrifugo connected', action.payload);
          yield put(setCentrifugoConnected());
          break;
        }
        case CENTRIFUGO_EVENTS_MAP.PUBLICATION: {
          yield call(publicationHandler, action.payload);
          break;
        }
        case CENTRIFUGO_EVENTS_MAP.ERROR: {
          logger.error('Error from centrifugo', action.payload);
          yield put(setCentrifugoError());
          break;
        }
        case CENTRIFUGO_EVENTS_MAP.DISCONNECT: {
          yield put(setCentrifugoDisconnected());
          logger.log('Ending work');
          return;
        }
        default: {
        }
      }
    }
  } catch (e) {
    logger.error('Error from centrifugoEventChannel', e);
  } finally {
    // used when emitter emits END event
    logger.info('Centrifugo disconnected');
  }
}
